import { SubmissionError } from 'redux-form';
import { get, omit } from 'lodash';

import createAsyncAction from 'shared/utils/createAsyncAction';
import { apiErrorHandler } from 'shared/utils/apiErrorHandler';
import { addToast } from 'modules/messageSystem/actions/addToast';
import { replaceTo } from 'modules/auth/actions/redirectAction';
import { hideAppointmentInfo } from 'modules/queues/common/actions/appointmentInfo';
import { fetchAppointmentById } from 'modules/queues/Schedule/actions/fetchAppointmentById';
import { WS_APPOINTMENTS_UPDATE as WS_APPOINTMENTS_UPDATE_SERVICE } from 'modules/websocket/services/wsAppointmentsUpdates';
import { CANCELED, CLOSED_APPOINTMENT_STATUSES } from 'modules/common/constants/appointmentStatuses';
import { isValidSpecialtyGroupProvider } from 'modules/appointmentsManagement/utils/appontmentFormUtils';
import {
  PAST_SCHEDULE_START_TIME,
  FLEXIBLE_SLOT_IS_TAKEN,
  apiErrorMessagesMap,
  modalErrors,
  cancelInProgress,
  IN_PROGRESS,
} from '../constants/apiErrorCodes';
import { FLEXIBLE } from '../constants/schedulingTypes';
import { UPDATE_APPOINTMENT_FORM } from '../constants/formTypes';
import { updateAppointment } from './_api/updateAppointment';
import { invokeAppointmentErrorModal } from './invokeModals';
import { checkAndCreateOrUpdateUser } from './createAppointment';
import createAction from './index';
import { isCurrentAppointmentGrouped } from '../../appointmentInfo/selectors/currentAppointment';
import { subscribeWS } from '../../websocket/WSManager';


export const SUBMIT_UPDATE_APPOINTMENT_FORM = createAsyncAction(createAction('SUBMIT_UPDATE_APPOINTMENT_FORM'));

export const submitUpdateAppointmentForm = (formData, isMobile) => async (dispatch, getState) => {
  const isCancel = formData.status === CANCELED;
  const data = { ...formData };
  const state = getState();
  const serviceConfig = get(state, 'appointmentInfo.currentAppointment.service.config');

  dispatch({ type: SUBMIT_UPDATE_APPOINTMENT_FORM.pending });

  if (data.conversationType) data.allowedConversationTypes = [data.conversationType];

  if (data.timeSlot) {
    const slots = data.timeSlot.split('-');
    data.schedule = {
      startTimestamp: Number(slots[0]),
      endTimestamp: Number(slots[1]),
    };
    delete data.timeSlot;
  } else if (data.flexible) {
    data.schedule = { startTimestamp: data.flexible.from };
    if (!serviceConfig.fixedAppointmentDuration) data.schedule.endTimestamp = data.flexible.to;
    delete data.flexible;
  }

  if ('providerUserId' in data) {
    if (isValidSpecialtyGroupProvider(data.providerUserId)) {
        data.providerGroupSpecialtyId = data.providerUserId;
    } else {
      data.providerGroupSpecialtyId = null;
    }
    if (data.providerUserId === 'PROVIDERS_GROUP' || data.providerGroupSpecialtyId) data.providerUserId = null;
  }

  await dispatch(checkAndCreateOrUpdateUser(
    data.customer,
    'customer',
    null,
    formData.organizationId,
    SUBMIT_UPDATE_APPOINTMENT_FORM,
    UPDATE_APPOINTMENT_FORM,
  ));

  return dispatch(updateAppointment(omit(data, 'customer', 'patient')))
    .then(({ payload }) => {
      dispatch({ type: SUBMIT_UPDATE_APPOINTMENT_FORM.success, payload });

      if (isCancel) {
        if (isMobile) return replaceTo(`/appointment/update/${ data.appointmentId }/cancel`);

        if (!isCurrentAppointmentGrouped(state)) {
          dispatch(hideAppointmentInfo());
          replaceTo('/appointments?list=true');
        } else {
          const { currentAppointment } = state.appointmentInfo;
          const tempApptGroup = currentAppointment.group.appointments.filter(
            tempAppt => !CLOSED_APPOINTMENT_STATUSES.includes(tempAppt.status),
          );

          if (tempApptGroup.length < 2) {
            dispatch(hideAppointmentInfo());
            replaceTo('/appointments?list=true');
          } else {
            replaceTo('/appointments');
          }

          return;
        }

        dispatch(addToast('success', 'appt-update-success', {
          id: 'Patient.Appointment.CancelAppointment.SuccessSnackbar',
          defaultMessage: '** Appointment cancelled successfully',
        }));
      } else {
        if (!isMobile) {
          replaceTo('/appointments');
        } else {
          return replaceTo(`/appointment/update/${ data.appointmentId }/success`);
        }

        dispatch(addToast('success', 'appt-update-success', {
          id: 'Patient.Appointment.EditAppointment.SuccessSnackbar',
          defaultMessage: '** Appointment updated successfully',
        }));
      }
    })
    .catch(error => {
      const apiErrorMessagesMapCopy = { ...apiErrorMessagesMap };

      dispatch({
        type: SUBMIT_UPDATE_APPOINTMENT_FORM.failure,
        meta: { errors: apiErrorHandler(error, false, apiErrorMessagesMapCopy, true) },
      });

      if (serviceConfig.schedulingType === FLEXIBLE) {
        apiErrorMessagesMapCopy[PAST_SCHEDULE_START_TIME] = apiErrorMessagesMapCopy[FLEXIBLE_SLOT_IS_TAKEN];
      }

      if (isCancel) {
        apiErrorMessagesMapCopy[IN_PROGRESS] = cancelInProgress;
      }

      const errors = apiErrorHandler(error, true, apiErrorMessagesMapCopy, false);

      if (Object.values(errors).some(value => modalErrors.includes(+value))) {
        const errorCode = Object.values(errors).find(value => modalErrors.includes(+value));

        return dispatch(invokeAppointmentErrorModal(apiErrorMessagesMapCopy[errorCode]));
      }

      throw new SubmissionError(errors);
    });
};

const WS_CURRENT_APPOINTMENTS_UPDATE = 'WS_CURRENT_APPOINTMENTS_UPDATE';

export const startObserveCurrentAppointmentUpdates = () => (dispatch, getState) =>
  subscribeWS(
    WS_APPOINTMENTS_UPDATE_SERVICE,
    { userId: getState().auth.userInfo.userId },
    ({ data: { appointmentUpdate: { appointmentId } } }) => {
      const currAppointmentId = get(getState(), 'appointmentInfo.currentAppointment.appointmentId');
      if (currAppointmentId !== appointmentId) return;

      return dispatch(fetchAppointmentById(appointmentId, true, true));
    },
    WS_CURRENT_APPOINTMENTS_UPDATE,
  );
